export interface ValuationSaleInput {
  saleDate: string | Date;
  salePrice: number;
  propertyType?: string | null;
  // Identity for cross-source sale-event dedupe. When two sources (SQM
  // + a scraped portal) report the same sale, both rows carry the same
  // saleEventKey so the valuation pipeline counts the event once.
  saleEventKey?: string | null;
}

export interface MonthlyMovementOptions {
  months?: number;
  referenceDate?: Date;
}

export interface MonthlyMovementResult {
  saleMonth: string;
  averagePrice: number | null;
  medianPrice: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  sampleCount: number;
  rawSampleCount: number;
  outliersRemoved: number;
  previousAveragePrice: number | null;
  priceChange: number | null;
  percentageChange: number | null;
}

export type MonthlyMovementStepSlug =
  | "input_filtering"
  | "monthly_grouping"
  | "outlier_removal"
  | "monthly_aggregation";

export interface MonthlyMovementTraceStep {
  slug: MonthlyMovementStepSlug;
  label: string;
  inputCount: number;
  outputCount: number;
  warnings: string[];
  summary: Record<string, unknown>;
}

export interface MonthlyMovementMonthTrace {
  saleMonth: string;
  rawPriceCount: number;
  outliersRemoved: number;
  samplePriceCount: number;
  minPrice: number | null;
  maxPrice: number | null;
  averagePrice: number | null;
  medianPrice: number | null;
  // Formula text rendered in the trace modal. Stored verbatim so the
  // UI doesn't reconstruct it from row counts.
  formula: string;
  formulaInputs: { n: number; sum: number };
}

export interface MonthlyMovementTrace {
  startMonth: string;
  endExclusiveMonth: string;
  steps: MonthlyMovementTraceStep[];
  perMonth: MonthlyMovementMonthTrace[];
  // Window-spanning roll-ups for apples-to-apples comparison with
  // external "rolling 12-mo" benchmarks. Computed across ALL deduped
  // sales in the window, not per-month, so they survive months with
  // sample_count < 4 (which the per-month IQR step leaves untouched).
  windowMedianPrice: number | null;
  windowMeanPrice: number | null;
  windowSampleCount: number;
}

export type ValuationDataSourceStatus =
  | "active"
  | "planned"
  | "pending_contract"
  | "disabled"
  | "error";

export type ValuationDataSourceCategory =
  | "internal"
  | "commercial_feed"
  | "file_import"
  | "official_api"
  | "official_bulk"
  | "bulk_reference";

export type ValuationDataSourceAccessMode =
  | "database"
  | "file_upload"
  | "api"
  | "bulk_file"
  | "manual";

export type ValuationConnectionStatus = "ok" | "warning" | "error" | "needs_secret" | "skipped";

export interface ValuationDataSourceRecord {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  category: ValuationDataSourceCategory;
  access_mode: ValuationDataSourceAccessMode;
  status: ValuationDataSourceStatus;
  provider: string | null;
  homepage_url: string | null;
  documentation_url: string | null;
  health_url: string | null;
  auth_required: boolean;
  secret_name: string | null;
  cadence: string | null;
  spatial_resolution: string | null;
  temporal_resolution: string | null;
  default_config: Record<string, unknown>;
  schema_hint: Record<string, unknown>;
  supports_live_fetch: boolean;
  supports_ingress: boolean;
  feeds_valuation: boolean;
  sort_order: number;
  latest_check_status: ValuationConnectionStatus | null;
  latest_check_at: string | null;
  latest_period: string | null;
  last_ingested_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ValuationDataConnectionCheck {
  id: string;
  source_id: string;
  status: ValuationConnectionStatus;
  http_status: number | null;
  latency_ms: number | null;
  message: string | null;
  details: Record<string, unknown>;
  checked_by: string | null;
  checked_at: string;
}

export interface ValuationIngressRun {
  id: string;
  source_id: string;
  status: "running" | "completed" | "failed";
  mode: "file_upload" | "api_fetch" | "manual";
  source_file_name: string | null;
  source_state: string | null;
  chunk_index: number | null;
  total_chunks: number | null;
  row_count: number;
  imported_count: number;
  rejected_count: number;
  duplicate_count: number;
  warning_count: number;
  sample_rows: SqmImportPreviewRow[];
  warnings: string[];
  error_message: string | null;
  created_by: string | null;
  started_at: string;
  completed_at: string | null;
  created_at: string;
  valuation_data_sources?: Pick<ValuationDataSourceRecord, "slug" | "name"> | null;
}

export interface SqmImportPreviewRow {
  rowNumber: number;
  streetAddress: string;
  suburb: string;
  postcode: string;
  soldPrice: number | null;
  soldDate: string | null;
  beds: number | null;
  baths: number | null;
  cars: number | null;
  areaSqm: number | null;
  parseWarnings: string[];
  // Groups likely-same-sale records together (state + addr + suburb +
  // postcode + sold_date + sold_price). NOT unique — multiple SQM rows
  // for the same sale event are allowed.
  saleEventKey: string;
}

export interface SqmSalesRecordInput extends SqmImportPreviewRow {
  sourceFileName: string;
  sourceState: string | null;
  addressKey: string;
  // Display-friendly row-identity key: `<file>#<state>#<rowNumber>`.
  // Uniqueness in the DB is enforced by (ingress_run_id, rowNumber).
  sourceRowKey: string;
  rawRow: Record<string, string>;
  rawLine: string;
}

export interface SqmCsvParseOptions {
  sourceFileName: string;
  sourceState?: string | null;
  maxRows?: number;
  // Offset added to each row's within-chunk lineIndex so `rowNumber` is
  // globally unique across chunks of the same file. Default 0 (single-
  // chunk uploads). The chunk splitter computes this as the 1-based
  // line number of the first data row in the chunk.
  baseRowNumber?: number;
}

export interface SqmCsvParseResult {
  rows: SqmSalesRecordInput[];
  rejected: Array<{
    rowNumber: number;
    rawRow: Record<string, string>;
    rawLine: string;
    parseWarnings: string[];
  }>;
  totalRowsRead: number;
  rejectedRows: number;
  warnings: string[];
  headers: string[];
}

export interface SqmRawArchiveRow {
  sourceRowNumber: number;
  rawRow: Record<string, string>;
  rawLine: string;
  parseWarnings: string[];
  parseStatus: "accepted" | "rejected";
  saleEventKey: string | null;
}

export function buildSqmRawArchiveRows(result: SqmCsvParseResult): SqmRawArchiveRow[] {
  const archiveRows: SqmRawArchiveRow[] = [
    ...result.rows.map((row) => ({
      sourceRowNumber: row.rowNumber,
      rawRow: row.rawRow,
      rawLine: row.rawLine,
      parseWarnings: row.parseWarnings,
      parseStatus: "accepted" as const,
      saleEventKey: row.saleEventKey,
    })),
    ...result.rejected.map((row) => ({
      sourceRowNumber: row.rowNumber,
      rawRow: row.rawRow,
      rawLine: row.rawLine,
      parseWarnings: row.parseWarnings,
      parseStatus: "rejected" as const,
      saleEventKey: null,
    })),
  ].sort((a, b) => a.sourceRowNumber - b.sourceRowNumber);
  if (archiveRows.length !== result.totalRowsRead) {
    throw new Error("SQM raw archive would omit parsed CSV rows");
  }
  return archiveRows;
}

/**
 * `sqm_sales_records` insert rows for the accepted lines. Deliberately no
 * `raw_row`: the source line lives once, in `sqm_ingress_raw_rows`, at
 * (ingress_run_id, source_row_number); the column keeps its `{}` default.
 */
export interface SqmStagingRow {
  source_id: string;
  ingress_run_id: string;
  source_file_name: string;
  source_state: string | null;
  source_row_number: number;
  source_row_key: string;
  street_address: string;
  suburb: string;
  postcode: string;
  sold_price: number | null;
  sold_date: string | null;
  beds: number | null;
  baths: number | null;
  cars: number | null;
  area_sqm: number | null;
  address_key: string;
  sale_event_key: string | null;
  parse_warnings: string[];
}

export function buildSqmStagingRows(
  result: SqmCsvParseResult,
  ids: { sourceId: string; ingressRunId: string },
): SqmStagingRow[] {
  return result.rows.map((row) => ({
    source_id: ids.sourceId,
    ingress_run_id: ids.ingressRunId,
    source_file_name: row.sourceFileName,
    source_state: row.sourceState,
    source_row_number: row.rowNumber,
    source_row_key: row.sourceRowKey,
    street_address: row.streetAddress,
    suburb: row.suburb,
    postcode: row.postcode,
    sold_price: row.soldPrice,
    sold_date: row.soldDate,
    beds: row.beds,
    baths: row.baths,
    cars: row.cars,
    area_sqm: row.areaSqm,
    address_key: row.addressKey,
    sale_event_key: row.saleEventKey || null,
    parse_warnings: row.parseWarnings,
  }));
}

export interface ValuationSourceCoverage {
  sourceSlug: string;
  sourceName: string;
  status: ValuationDataSourceStatus;
  recordCount: number;
  pricedRecordCount?: number;
  latestPeriod?: string | null;
  coverageLabel: string;
  notes?: string | null;
}

export interface WeightedComparableInput {
  saleDate: string | Date;
  salePrice: number;
  suburb?: string | null;
  postcode?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  parking?: number | null;
  sourceConfidence?: number | null;
}

export interface WeightedComparableOptions {
  targetSuburb?: string | null;
  targetPostcode?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  parking?: number | null;
  referenceDate?: Date;
  maxAgeMonths?: number;
  minComparables?: number;
}

export interface WeightedComparableEstimate {
  estimatedPrice: number | null;
  comparableCount: number;
  weightedSampleCount: number;
  confidenceScore: number;
  minPrice: number | null;
  maxPrice: number | null;
  warnings: string[];
}

const HOUSE_TYPE_TERMS = ["house", "home", "residence", "cottage", "homestead"];
const SQM_EXPECTED_HEADERS = [
  "street_address",
  "suburb",
  "postcode",
  "sold_price",
  "sold_date",
  "beds",
  "baths",
  "cars",
  "area",
];

export function isHouseLikePropertyType(propertyType: string | null | undefined): boolean {
  if (!propertyType) {
    return false;
  }

  const normalized = propertyType.toLowerCase();
  return HOUSE_TYPE_TERMS.some((term) => normalized.includes(term));
}

export function isConfirmedHouseSale(
  propertyType: string | null | undefined,
  adapterSlug: string | null | undefined,
): boolean {
  // Historical SQM snapshots carry a fabricated 'house' label until a
  // separately reviewed correction runs. SQM itself supplies no type.
  return adapterSlug !== "sqm-back-history" && isHouseLikePropertyType(propertyType);
}

/**
 * listing_type of a completed sale. property_snapshots rows of any other
 * type ('buy', 'auction', 'under offer', ...) can carry sale_date and
 * sale_price too, but those hold an asking price or auction date, not a
 * sale. Queries filter with `.ilike("listing_type", SOLD_LISTING_TYPE)`.
 */
export const SOLD_LISTING_TYPE = "sold";

export function isSoldListingType(listingType: string | null | undefined): boolean {
  return listingType?.toLowerCase() === SOLD_LISTING_TYPE;
}

export function housePvUnavailableReason(
  sourceMode: string,
  confirmedSaleCount?: number,
): string | null {
  if (sourceMode === "sqm_only") {
    return "SQM supplies no confirmed property types; house PV cannot run with SQM only.";
  }
  if (confirmedSaleCount === 0) {
    return "No confirmed house sales matched these filters; no PV result was produced.";
  }
  return null;
}

function toFinitePrice(value: number): number | null {
  return Number.isFinite(value) && value > 0 ? value : null;
}

function toMonthKey(value: string | Date): string | null {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  return `${year}-${month}-01`;
}

function startOfUtcMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

export function getCompleteMonthWindow(options: MonthlyMovementOptions = {}): {
  startMonth: string;
  endExclusiveMonth: string;
} {
  const months = Math.max(1, Math.min(options.months ?? 12, 120));
  const endExclusive = startOfUtcMonth(options.referenceDate ?? new Date());
  const start = new Date(endExclusive);
  start.setUTCMonth(start.getUTCMonth() - months);

  return {
    startMonth: toMonthKey(start) ?? start.toISOString().slice(0, 10),
    endExclusiveMonth: toMonthKey(endExclusive) ?? endExclusive.toISOString().slice(0, 10),
  };
}

function quantile(sortedValues: number[], position: number): number {
  if (sortedValues.length === 0) {
    return 0;
  }

  const index = (sortedValues.length - 1) * position;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) {
    return sortedValues[lower];
  }

  return sortedValues[lower] + (sortedValues[upper] - sortedValues[lower]) * (index - lower);
}

function median(sortedValues: number[]): number {
  return quantile(sortedValues, 0.5);
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundPercent(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

function removeIqrOutliers(values: number[]): { kept: number[]; removed: number } {
  if (values.length < 4) {
    return { kept: values, removed: 0 };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const q1 = quantile(sorted, 0.25);
  const q3 = quantile(sorted, 0.75);
  const iqr = q3 - q1;
  const lower = q1 - iqr * 1.5;
  const upper = q3 + iqr * 1.5;
  const kept = values.filter((value) => value >= lower && value <= upper);

  return { kept, removed: values.length - kept.length };
}

/**
 * Cross-source sale-event dedupe. Each input row may carry an explicit
 * `saleEventKey` (built from state + addr + suburb + postcode + date +
 * price by the SQM parser, or sourced from
 * property_snapshots.raw_data.sqmSaleEventKey for SQM-backfilled
 * snapshots). When present, we count one event per key. When absent,
 * fall back to (date, price-bucket) which catches cross-source dupes
 * lacking a SQM provenance trail. Two genuinely distinct sales at the
 * same suburb + day + rounded price are rare enough that this is
 * acceptable in the absence of a real key.
 */
export function dedupeSalesByEvent(sales: ValuationSaleInput[]): {
  deduped: ValuationSaleInput[];
  duplicatesDropped: number;
} {
  const seen = new Set<string>();
  const deduped: ValuationSaleInput[] = [];
  let duplicatesDropped = 0;
  for (const sale of sales) {
    const dateStr =
      typeof sale.saleDate === "string"
        ? sale.saleDate.slice(0, 10)
        : sale.saleDate instanceof Date
          ? sale.saleDate.toISOString().slice(0, 10)
          : null;
    if (!dateStr) {
      duplicatesDropped += 1;
      continue;
    }
    const priceBucket = Math.round(sale.salePrice / 1000) * 1000;
    const key = sale.saleEventKey ? `evt:${sale.saleEventKey}` : `dp:${dateStr}|${priceBucket}`;
    if (seen.has(key)) {
      duplicatesDropped += 1;
      continue;
    }
    seen.add(key);
    deduped.push(sale);
  }
  return { deduped, duplicatesDropped };
}

/**
 * Traced variant of the monthly movement algorithm. Returns the same
 * MonthlyMovementResult[] as computeMonthlyMovement, plus a compact
 * execution trace the run lab persists to `valuation_run_steps` and
 * embeds into per-result `provenance` JSONB.
 *
 * Trace shape is small by design: the UI fetches it on every run detail
 * open. Full row-level drill-down (which exact sales contributed to
 * March 2025) is computed live by the trace-result edge action, NOT
 * stored here.
 */
export function computeMonthlyMovementWithTrace(
  sales: ValuationSaleInput[],
  options: MonthlyMovementOptions = {},
): { output: MonthlyMovementResult[]; trace: MonthlyMovementTrace } {
  const { startMonth, endExclusiveMonth } = getCompleteMonthWindow(options);

  let outOfWindow = 0;
  let invalidPriceOrDate = 0;
  const grouped = new Map<string, number[]>();

  for (const sale of sales) {
    const monthKey = toMonthKey(sale.saleDate);
    const price = toFinitePrice(sale.salePrice);
    if (!monthKey || !price) {
      invalidPriceOrDate += 1;
      continue;
    }
    if (monthKey < startMonth || monthKey >= endExclusiveMonth) {
      outOfWindow += 1;
      continue;
    }
    const values = grouped.get(monthKey) ?? [];
    values.push(price);
    grouped.set(monthKey, values);
  }

  const totalRawAccepted = [...grouped.values()].reduce((sum, list) => sum + list.length, 0);

  const inputFilteringStep: MonthlyMovementTraceStep = {
    slug: "input_filtering",
    label: "Input filtering",
    inputCount: sales.length,
    outputCount: totalRawAccepted,
    warnings: [],
    summary: {
      windowStart: startMonth,
      windowEndExclusive: endExclusiveMonth,
      droppedInvalid: invalidPriceOrDate,
      droppedOutOfWindow: outOfWindow,
    },
  };

  const monthlyGroupingStep: MonthlyMovementTraceStep = {
    slug: "monthly_grouping",
    label: "Monthly grouping",
    inputCount: totalRawAccepted,
    outputCount: grouped.size,
    warnings: [],
    summary: {
      monthsWithData: grouped.size,
    },
  };

  let totalOutliersRemoved = 0;
  let totalKept = 0;
  let previousAveragePrice: number | null = null;
  const perMonth: MonthlyMovementMonthTrace[] = [];
  const output: MonthlyMovementResult[] = [];

  const sortedMonths = [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));

  for (const [saleMonth, rawValues] of sortedMonths) {
    const { kept, removed } = removeIqrOutliers(rawValues);
    totalOutliersRemoved += removed;
    totalKept += kept.length;
    const sorted = [...kept].sort((a, b) => a - b);
    const sum = sorted.reduce((acc, value) => acc + value, 0);
    const averagePrice = sorted.length ? roundMoney(sum / sorted.length) : null;
    const medianPrice = sorted.length ? roundMoney(median(sorted)) : null;
    const minPrice = sorted.length ? sorted[0] : null;
    const maxPrice = sorted.length ? sorted[sorted.length - 1] : null;
    const priceChange =
      averagePrice !== null && previousAveragePrice !== null
        ? roundMoney(averagePrice - previousAveragePrice)
        : null;
    const percentageChange =
      averagePrice !== null && previousAveragePrice
        ? roundPercent(((averagePrice - previousAveragePrice) / previousAveragePrice) * 100)
        : null;

    output.push({
      saleMonth,
      averagePrice,
      medianPrice,
      minPrice,
      maxPrice,
      sampleCount: sorted.length,
      rawSampleCount: rawValues.length,
      outliersRemoved: removed,
      previousAveragePrice,
      priceChange,
      percentageChange,
    });

    perMonth.push({
      saleMonth,
      rawPriceCount: rawValues.length,
      outliersRemoved: removed,
      samplePriceCount: sorted.length,
      minPrice,
      maxPrice,
      averagePrice,
      medianPrice,
      formula:
        sorted.length > 0
          ? `average = sum(${sorted.length} prices) / ${sorted.length}`
          : "average = n/a (no sales in window)",
      formulaInputs: { n: sorted.length, sum },
    });

    if (averagePrice !== null) {
      previousAveragePrice = averagePrice;
    }
  }

  const outlierStep: MonthlyMovementTraceStep = {
    slug: "outlier_removal",
    label: "IQR outlier removal",
    inputCount: totalRawAccepted,
    outputCount: totalKept,
    warnings: [],
    summary: {
      totalOutliersRemoved,
      method: "Tukey 1.5*IQR fence; skipped when month has <4 sales",
    },
  };

  const aggregationStep: MonthlyMovementTraceStep = {
    slug: "monthly_aggregation",
    label: "Monthly aggregation",
    inputCount: totalKept,
    outputCount: output.length,
    warnings: [],
    summary: {
      monthsReturned: output.length,
    },
  };

  // Window-spanning roll-up. Take every accepted price across the run
  // (post-input-filter, pre-monthly-grouping) and compute one median +
  // mean. This is the comparator that matches Domain/REA "rolling 12-mo
  // median over all sales" — per-month aggregates are noisy in low-volume
  // suburbs and skip months with no sales, which distorts long-window
  // comparisons. We deliberately do NOT apply IQR at the window level —
  // the per-month IQR already removes within-month extremes; running it
  // again across all months would over-trim.
  const windowPrices: number[] = [];
  for (const list of grouped.values()) {
    for (const price of list) {
      windowPrices.push(price);
    }
  }
  windowPrices.sort((a, b) => a - b);
  const windowSampleCount = windowPrices.length;
  const windowMedianPrice = windowSampleCount > 0 ? roundMoney(median(windowPrices)) : null;
  const windowMeanPrice =
    windowSampleCount > 0
      ? roundMoney(windowPrices.reduce((sum, value) => sum + value, 0) / windowSampleCount)
      : null;

  return {
    output,
    trace: {
      startMonth,
      endExclusiveMonth,
      steps: [inputFilteringStep, monthlyGroupingStep, outlierStep, aggregationStep],
      perMonth,
      windowMedianPrice,
      windowMeanPrice,
      windowSampleCount,
    },
  };
}

/**
 * Backwards-compat alias. The traced variant is the source of truth;
 * this wrapper exists because `@repo/types` is shared between the edge
 * function, the browser app, and any preview code paths that don't
 * need the trace.
 */
export function computeMonthlyMovement(
  sales: ValuationSaleInput[],
  options: MonthlyMovementOptions = {},
): MonthlyMovementResult[] {
  return computeMonthlyMovementWithTrace(sales, options).output;
}

export function normalizeValuationText(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = line[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells;
}

function parseNullableInteger(value: string | null | undefined): number | null {
  const cleaned = (value ?? "").trim();
  if (!cleaned) {
    return null;
  }

  const parsed = parseInt(cleaned, 10);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function parseNullableDecimal(value: string | null | undefined): number | null {
  const cleaned = (value ?? "").replace(/[$,\s]/g, "");
  if (!cleaned) {
    return null;
  }

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

export function parseSqmSoldDate(value: string | null | undefined): string | null {
  const cleaned = (value ?? "").trim();
  const match = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) {
    return null;
  }

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date.toISOString().slice(0, 10);
}

function buildSqmAddressKey(row: {
  streetAddress: string;
  suburb: string;
  sourceState: string | null;
  postcode: string;
}): string {
  return normalizeValuationText(
    `${row.streetAddress} ${row.suburb} ${row.sourceState ?? ""} ${row.postcode}`,
  );
}

// Groups SQM rows that look like the same sale event (joint sales,
// vendor duplicates). NOT a row-identity key — multiple records may
// share this; valuation logic dedupes by this at calc time.
function buildSqmSaleEventKey(row: {
  sourceState: string | null;
  streetAddress: string;
  suburb: string;
  postcode: string;
  soldDate: string | null;
  soldPrice: number | null;
}): string {
  return [
    row.sourceState ?? "",
    normalizeValuationText(row.streetAddress),
    normalizeValuationText(row.suburb),
    row.postcode,
    row.soldDate ?? "",
    row.soldPrice ?? "",
  ].join("|");
}

function recordFromCells(headers: string[], cells: string[]): Record<string, string> {
  return headers.reduce<Record<string, string>>((record, header, index) => {
    record[header] = cells[index] ?? "";
    return record;
  }, {});
}

function validateSqmHeaders(headers: string[]): string[] {
  return SQM_EXPECTED_HEADERS.filter((header) => !headers.includes(header));
}

export function parseSqmCsv(text: string, options: SqmCsvParseOptions): SqmCsvParseResult {
  const lines = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);
  const warnings: string[] = [];
  if (lines.length === 0) {
    return {
      rows: [],
      rejected: [],
      totalRowsRead: 0,
      rejectedRows: 0,
      warnings: ["CSV is empty"],
      headers: [],
    };
  }

  const headers = parseCsvLine(lines[0]).map((header) => header.replace(/^\uFEFF/, "").trim());
  const missingHeaders = validateSqmHeaders(headers);
  if (missingHeaders.length > 0) {
    warnings.push(`Missing expected columns: ${missingHeaders.join(", ")}`);
  }

  const rows: SqmSalesRecordInput[] = [];
  const rejected: SqmCsvParseResult["rejected"] = [];
  let rejectedRows = 0;
  const maxRows = Math.max(1, Math.min(options.maxRows ?? Number.POSITIVE_INFINITY, 100_000));
  const baseRowNumber = Math.max(0, options.baseRowNumber ?? 0);

  for (let lineIndex = 1; lineIndex < lines.length && lineIndex <= maxRows; lineIndex += 1) {
    // baseRowNumber is the 1-based line-number of the FIRST data row of
    // this chunk (splitter passes `index` from its slice loop). For the
    // first chunk (baseRowNumber=1), rowNumber for lineIndex=1 should
    // be 1. For chunk-2 with baseRowNumber=2501, lineIndex=1 → 2501.
    const rowNumber = baseRowNumber > 0 ? baseRowNumber + lineIndex - 1 : lineIndex + 1;
    const rawRow = recordFromCells(headers, parseCsvLine(lines[lineIndex]));
    const streetAddress = rawRow.street_address?.trim() ?? "";
    const suburb = rawRow.suburb?.trim() ?? "";
    const postcode = rawRow.postcode?.trim() ?? "";
    const sourceState = options.sourceState?.trim().toUpperCase() || null;
    const soldPrice = parseNullableDecimal(rawRow.sold_price);
    const soldDate = parseSqmSoldDate(rawRow.sold_date);
    const beds = parseNullableInteger(rawRow.beds);
    const baths = parseNullableInteger(rawRow.baths);
    const cars = parseNullableInteger(rawRow.cars);
    const areaSqm = parseNullableDecimal(rawRow.area);
    const parseWarnings: string[] = [];

    if (!streetAddress) parseWarnings.push("missing_address");
    if (!suburb) parseWarnings.push("missing_suburb");
    if (!postcode) parseWarnings.push("missing_postcode");
    if (!soldPrice) parseWarnings.push("missing_or_invalid_sold_price");
    if (!soldDate) parseWarnings.push("missing_or_invalid_sold_date");
    if (rawRow.beds && beds === null) parseWarnings.push("invalid_beds");
    if (rawRow.baths && baths === null) parseWarnings.push("invalid_baths");
    if (rawRow.cars && cars === null) parseWarnings.push("invalid_cars");
    if (rawRow.area && areaSqm === null) parseWarnings.push("invalid_area");

    if (!streetAddress || !suburb || !postcode) {
      rejectedRows += 1;
      rejected.push({ rowNumber, rawRow, rawLine: lines[lineIndex], parseWarnings });
      continue;
    }

    const baseRow = {
      sourceState,
      streetAddress,
      suburb,
      postcode,
      soldDate,
      soldPrice,
    };
    const addressKey = buildSqmAddressKey(baseRow);
    const saleEventKey = buildSqmSaleEventKey(baseRow);
    const sourceRowKey = `${options.sourceFileName}#${sourceState ?? "?"}#${rowNumber}`;

    rows.push({
      rowNumber,
      sourceFileName: options.sourceFileName,
      sourceState,
      streetAddress,
      suburb,
      postcode,
      soldPrice,
      soldDate,
      beds,
      baths,
      cars,
      areaSqm,
      addressKey,
      saleEventKey,
      sourceRowKey,
      rawRow,
      rawLine: lines[lineIndex],
      parseWarnings,
    });
  }

  return {
    rows,
    rejected,
    totalRowsRead: Math.max(lines.length - 1, 0),
    rejectedRows,
    warnings,
    headers,
  };
}

function monthsBetween(start: Date, end: Date): number {
  return Math.max(
    0,
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + end.getUTCMonth() - start.getUTCMonth(),
  );
}

function featureSimilarityWeight(
  target: number | null | undefined,
  actual: number | null | undefined,
): number {
  if (target === null || target === undefined || actual === null || actual === undefined) {
    return 0.85;
  }
  if (target === actual) {
    return 1;
  }
  const distance = Math.abs(target - actual);
  return Math.max(0.25, 1 - distance * 0.2);
}

export function computeWeightedComparableEstimate(
  comparables: WeightedComparableInput[],
  options: WeightedComparableOptions = {},
): WeightedComparableEstimate {
  const referenceDate = options.referenceDate ?? new Date();
  const maxAgeMonths = Math.max(1, Math.min(options.maxAgeMonths ?? 36, 240));
  const targetSuburb = normalizeValuationText(options.targetSuburb);
  const targetPostcode = options.targetPostcode?.trim() ?? null;
  const weighted: Array<{ price: number; weight: number }> = [];
  const warnings: string[] = [];

  for (const comparable of comparables) {
    const price = toFinitePrice(comparable.salePrice);
    const date =
      comparable.saleDate instanceof Date ? comparable.saleDate : new Date(comparable.saleDate);
    if (!price || Number.isNaN(date.getTime())) {
      continue;
    }

    const ageMonths = monthsBetween(date, referenceDate);
    if (ageMonths > maxAgeMonths) {
      continue;
    }

    const recencyWeight = Math.max(0.2, 1 - ageMonths / (maxAgeMonths + 1));
    const suburbWeight =
      targetSuburb && normalizeValuationText(comparable.suburb) === targetSuburb ? 1.15 : 0.85;
    const postcodeWeight =
      targetPostcode && comparable.postcode?.trim() === targetPostcode ? 1.1 : 0.8;
    const bedroomsWeight = featureSimilarityWeight(options.bedrooms, comparable.bedrooms);
    const bathroomsWeight = featureSimilarityWeight(options.bathrooms, comparable.bathrooms);
    const parkingWeight = featureSimilarityWeight(options.parking, comparable.parking);
    const sourceWeight = Math.max(0.25, Math.min(comparable.sourceConfidence ?? 1, 1));
    const weight =
      recencyWeight *
      suburbWeight *
      postcodeWeight *
      bedroomsWeight *
      bathroomsWeight *
      parkingWeight *
      sourceWeight;

    weighted.push({ price, weight });
  }

  const minComparables = Math.max(1, options.minComparables ?? 3);
  if (weighted.length < minComparables) {
    warnings.push(`Only ${weighted.length} comparable sales matched the requested segment.`);
  }
  if (weighted.length === 0) {
    return {
      estimatedPrice: null,
      comparableCount: 0,
      weightedSampleCount: 0,
      confidenceScore: 0,
      minPrice: null,
      maxPrice: null,
      warnings,
    };
  }

  const totalWeight = weighted.reduce((sum, item) => sum + item.weight, 0);
  const estimatedPrice = roundMoney(
    weighted.reduce((sum, item) => sum + item.price * item.weight, 0) / totalWeight,
  );
  const prices = weighted.map((item) => item.price).sort((a, b) => a - b);
  const confidenceScore = Math.round(
    Math.min(100, (weighted.length / minComparables) * 55 + Math.min(totalWeight, 10) * 4.5),
  );

  return {
    estimatedPrice,
    comparableCount: weighted.length,
    weightedSampleCount: roundMoney(totalWeight),
    confidenceScore,
    minPrice: prices[0],
    maxPrice: prices[prices.length - 1],
    warnings,
  };
}
